import styles from './styles.module.css'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { useState } from 'react'
import { Tag } from 'primereact/tag'
import { Toolbar } from 'primereact/toolbar'
import { Button } from 'primereact/button'
import DatePicker from '../datePicker/DatePicker'
import { FilterMatchMode } from "primereact/api";
import { RejectReasonDialog } from '../rejectReasonDialog/RejectReasonDialog'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { InputText } from 'primereact/inputtext'
import { Can, PERMISSIONS } from '../../security'


/**
 * ManagerRequestsTable component responsible for displaying time requests
 * (Manual Time and Overtime) and handling creation, cancellation
 * and deletion of those requests.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.header - Table header content.
 * @param {Array<Object>} props.data - Data source for the table.
 * @param {React.ReactNode} props.footer - Table footer content.
 * @param {Object} props.filters - DataTable filter configuration.
 * @param {Array<string>} props.globalFilterFields - Fields used in global search.
 * @param {Function} props.onSubmitted - Callback triggered after any request mutation.
 * @param {Function} props.onDateSelect - Callback triggered when month/year is selected.
 *
 * @returns {JSX.Element}
 */
function ManagerRequestsTable({data, globalFilterFields, onSubmitted, onDateSelect, defaultMonth, defaultYear, loading}){
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
    const [selectedYear, setSelectedYear] = useState(defaultYear);
    const [requestType, setRequestType] = useState(null);
    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date: { value: null, matchMode: FilterMatchMode.DATE_IS },
        name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        project: { value: null, matchMode: FilterMatchMode.STARTS_WITH }
    })
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    /**
     * Maps request status to PrimeReact Tag severity.
     *
     * @param {string} status - Request status.
     * @returns {string} PrimeReact severity value.
     */
    const getStatus = (status) => {
        switch (status) {
            case 'pending':
                return 'info';
            
            case "Approved":
                return 'success'

            case "Rejected":
                return 'danger'
        }
    }

    /**
     * Maps request type to PrimeReact Tag severity.
     *
     * @param {string} type - Request type.
     * @returns {string} PrimeReact severity value.
     */
    const getType = (type) => {
        switch (type) {
            case 'overtime':
                return 'info';
            
            case 'manualTime':
                return 'success'
        }
    }

    const handleOpenDialog = (rowData) => {
        setSelectedRequest(rowData)
        setOpenRejectDialog(true)
        setRequestType(rowData.type)
    }

    /**
     * Renders the Status column content.
     *
     * @param {Object} rowData - Row data from DataTable.
     * @returns {JSX.Element}
     */
    const statusBodyTemplate = (rowData) => {
        if(rowData.status === "Requested"){
            return (
                <div className={styles.tableActionBtnsBox}>
                    <Can permission={PERMISSIONS.requestsManualTimeUsersByUseridByRequestid_patch}>
                        <Tag className={styles.tableActionBtns} value="Approve" severity={'info'} onClick={() => handleApproveRequest(rowData)}/>

                    </Can>
                    <Tag className={styles.tableActionBtns} value="Reject" severity={'danger'} onClick={() => handleOpenDialog(rowData)}/>
                </div>
            )

        } else {
            return <Tag value={rowData.status} severity={getStatus(rowData.status)} />;
        }
    }

    /**
     * Renders the Type column content.
     *
     * @param {Object} rowData - Row data from DataTable.
     * @returns {JSX.Element}
     */
    const typeBodyTemplate = (rowData) => {
        return <Tag value={rowData.type} severity={getType(rowData.type)} />;
    }

     /**
     * Triggers data reload based on selected month and year.
     */
    const handleFindRequests = async() => {
        onDateSelect(selectedMonth, selectedYear)
    }

    const handleRejectReasonSelect = async(reasonId) => {
        onSubmitted(selectedRequest.id, selectedRequest.type, "rejected", reasonId)
        
        setOpenRejectDialog(false)
        setSelectedRequest(null)
        setRequestType(null)
    }
    
    const handleApproveRequest = async(rowData) => {
        onSubmitted(rowData.id, rowData.type, "approved")
    }

    /**
     * Handles month selection change.
     *
     * @param {number} month
     */
    const handleMonthChange = async(month) => {
        setSelectedMonth(month)
    }

    /**
     * Handles year selection change.
     *
     * @param {number} year
     */
    const handleYearChange = async(year) => {
        setSelectedYear(year)
    }

    /**
     * Toolbar template rendered on the left side.
     *
     * @returns {JSX.Element}
     */
    const leftToolbarTemplate = () => {
        return (
            <div className={styles.headerActions}>
                <DatePicker defaultMonth={defaultMonth} defaultYear={defaultYear} onMonthChange={handleMonthChange} onYearChange={handleYearChange}/>
                <Button label='Find' severity='success' onClick={handleFindRequests}/>
            </div>
        )
    }

    const rightToolbarTemplate = () => {
        return (
            <div className="flex justify-content-end">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                </IconField>
            </div>
        );
    }

    return(
        <div>
            <div className='card'>
                <Toolbar start={leftToolbarTemplate} end={rightToolbarTemplate}/>
                <DataTable value={data} paginator rows={5} dataKey="id" filters={filters} filterDisplay="row" loading={loading}
                    globalFilterFields={globalFilterFields} emptyMessage="No data found."
                >
                    <Column field='date' header='Date'/>
                    <Column field='type' header='Type' body={typeBodyTemplate}/>
                    <Column field='project' header='Project'/>
                    <Column field='startTime' header='Start Time'/>
                    <Column field='endTime' header='End Time'/>
                    <Column field='hours' header='Hours'/>
                    <Column field='reason' header='Reason'/>
                    <Column field='manager' header='Approver'/>
                    <Column field='approvalDate' header='Approved At'/>
                    <Column field='rejectReason' header='Reject Reason'/>
                    <Column field='status' header='Status' body={statusBodyTemplate}/>
                </DataTable>
            </div>
            <RejectReasonDialog 
                type={requestType}
                onClose={() => setOpenRejectDialog(false)}
                open={openRejectDialog}
                onReject={handleRejectReasonSelect}
            />
        </div>
    )
}

export default ManagerRequestsTable;